import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { MiniNotchVisualizer } from '../components/MiniNotchVisualizer';
import { hapticFeedback } from '../utils/haptic';

interface FAQItem {
  question: string;
  answer: string;
  category: 'about' | 'how' | 'therapy' | 'technical';
}

const faqData: FAQItem[] = [
  // About Tinnitus - Core Understanding
  {
    question: "What is tinnitus?",
    answer: "Ringing, buzzing, or hissing sounds only you can hear. Affects 1 in 5 people. Most can identify a specific frequency - that's where we help.",
    category: "about"
  },
  {
    question: "Why does knowing my frequency matter?",
    answer: "Your specific frequency enables targeted notched sound therapy. Research shows removing your exact frequency from white noise can reduce symptoms by 20% on average.",
    category: "about"
  },
  {
    question: "Can tinnitus frequency change?",
    answer: "Yes, it can fluctuate with stress, fatigue, or diet. Test when it's most noticeable for best results. Retest anytime - it's free and takes 5 minutes.",
    category: "about"
  },
  
  // How It Works
  {
    question: "How does TinnitusTune find my frequency?",
    answer: "Quick audio test using binary search - like 'higher or lower' but for sound. Takes 8-12 comparisons to pinpoint your exact frequency within ±5 Hz.",
    category: "how"
  },
  {
    question: "How long does testing take?",
    answer: "Under 5 minutes. You compare test tones to your tinnitus and click higher/lower. That's it.",
    category: "how"
  },
  {
    question: "Do I need headphones?",
    answer: "Yes - they ensure accurate frequency reproduction and eliminate room noise. Any decent headphones work.",
    category: "how"
  },
  {
    question: "What frequency range do you test?",
    answer: "250-12,000 Hz covers 90% of cases. Extended mode goes 20-20,000 Hz if needed.",
    category: "how"
  },
  
  // Therapy & Research
  {
    question: "What is notched sound therapy?",
    answer: "White noise with your tinnitus frequency removed. Your brain gradually reduces focus on the missing frequency, potentially decreasing tinnitus perception.",
    category: "therapy"
  },
  {
    question: "What research backs this?",
    answer: "Okamoto et al. (2010) in PNAS showed notched music reduced tinnitus loudness by reversing auditory cortex changes. Multiple studies since have shown positive results.",
    category: "therapy"
  },
  {
    question: "How often should I use the therapy?",
    answer: "Research suggests 1-2 hours daily. Use while working, reading, or relaxing. Consistency matters more than duration.",
    category: "therapy"
  },
  {
    question: "Will this cure my tinnitus?",
    answer: "No cure, but many experience reduced loudness and distress. Individual results vary. Works best as part of comprehensive management.",
    category: "therapy"
  },
  
  // Technical & Privacy
  {
    question: "Is my data private?",
    answer: "Your audio and frequency data stay in your browser - never recorded or sent anywhere. We use Google Analytics to understand general usage patterns (like page visits), but nothing that identifies you personally. No account needed, no personal info collected.",
    category: "technical"
  },
  {
    question: "Which browsers work?",
    answer: "All modern browsers - Chrome, Firefox, Safari, Edge. Mobile works great too. Just need headphones.",
    category: "technical"
  },
  {
    question: "Can I save my results?",
    answer: "Yes - results include a shareable link. Also saved locally so you can return anytime from the same device.",
    category: "technical"
  },
  {
    question: "Is this a medical device?",
    answer: "No - it's a frequency detection tool. Share results with your doctor for medical advice. We help you find the frequency, they help with treatment.",
    category: "technical"
  }
];

const AboutTinnitus: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'about' | 'how' | 'therapy' | 'technical'>('all');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const filteredFAQs = activeCategory === 'all' 
    ? faqData 
    : faqData.filter(item => item.category === activeCategory);

  const categories = [
    { id: 'all', label: 'All', icon: '📋' },
    { id: 'about', label: 'About', icon: '👂' },
    { id: 'how', label: 'How', icon: '🎯' },
    { id: 'therapy', label: 'Therapy', icon: '🎵' },
    { id: 'technical', label: 'Tech', icon: '⚙️' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Simple Back Button */}
      <div className="container mx-auto px-4 py-6">
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-8 flex-grow">
        <div className="max-w-4xl mx-auto">
          
          {/* Hero Section - Quick Summary */}
          <div className="bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 text-white rounded-2xl p-8 mb-8 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">
              Tinnitus? We Can Help.
            </h1>
            
            {/* Mini Visualizer */}
            <div className="mb-6">
              <MiniNotchVisualizer className="mx-auto" />
              <p className="text-sm opacity-90 mt-2">
                We remove your tinnitus frequency from white noise
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center mb-6">
              <div>
                <div className="text-3xl font-bold mb-1">50M+</div>
                <div className="text-sm opacity-90">Americans have it</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-1">70%</div>
                <div className="text-sm opacity-90">Can identify frequency</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-1">20%</div>
                <div className="text-sm opacity-90">Average reduction</div>
              </div>
            </div>
            
            <p className="text-lg mb-6 opacity-95 max-w-2xl mx-auto">
              Find your exact frequency. Get personalized sound therapy. 
              Based on research, not marketing.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/"
                onClick={() => hapticFeedback.buttonTap()}
                className="px-8 py-4 bg-white text-purple-600 font-bold rounded-full hover:bg-gray-50 transition-all duration-200 shadow-lg transform hover:scale-105"
              >
                Find Your Frequency →
              </Link>
              <Link 
                to="/notched-white-noise"
                onClick={() => hapticFeedback.buttonTap()}
                className="px-8 py-4 bg-transparent text-white font-bold rounded-full border-2 border-white hover:bg-white hover:text-purple-600 transition-all duration-200"
              >
                Skip to Therapy
              </Link>
            </div>
          </div>

          {/* Quick Facts Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              The Quick Version
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="font-semibold text-purple-700">What We Do</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">✓</span>
                    <span>Find your exact tinnitus frequency (5 min test)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">✓</span>
                    <span>Generate notched white noise therapy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">✓</span>
                    <span>Based on peer-reviewed research</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">✓</span>
                    <span>Free. No registration. No BS.</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-purple-700">How It Helps</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-pink-500 mt-1">→</span>
                    <span>Removes your frequency from sound spectrum</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-pink-500 mt-1">→</span>
                    <span>Brain gradually adapts to the "gap"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-pink-500 mt-1">→</span>
                    <span>Can reduce perceived loudness over time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-pink-500 mt-1">→</span>
                    <span>Listen 1-2 hours daily for best results</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Common Questions
            </h2>
            
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id as any)}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                    activeCategory === category.id
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-1">{category.icon}</span>
                  {category.label}
                </button>
              ))}
            </div>

            {/* FAQ Items */}
            <div className="space-y-3">
              {filteredFAQs.map((item, index) => {
                const globalIndex = faqData.indexOf(item);
                const isExpanded = expandedItems.has(globalIndex);
                
                return (
                  <div
                    key={globalIndex}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:border-purple-300 transition-colors"
                  >
                    <button
                      onClick={() => toggleItem(globalIndex)}
                      className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-purple-50 transition-colors"
                    >
                      <h3 className="font-semibold text-gray-800 pr-4">
                        {item.question}
                      </h3>
                      <span
                        className={`text-purple-500 text-xl transition-transform duration-200 flex-shrink-0 ${
                          isExpanded ? 'rotate-45' : ''
                        }`}
                      >
                        +
                      </span>
                    </button>
                    
                    {isExpanded && (
                      <div className="px-5 pb-4 pt-2 border-t border-gray-100">
                        <p className="text-gray-700 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              Ready to find relief?
            </p>
            <Link 
              to="/"
              onClick={() => hapticFeedback.buttonTap()}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold rounded-full hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              Start Free Test
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AboutTinnitus;